/**
 * A small on/off switch (role="switch") for the 24-Hour Line's admin tabs
 * (WP-11): the Team tab's active toggle and the Settings tab's booleans.
 * The CRM settings switch (crm/pages/settings/BookingSettings.tsx) is the
 * look; a 44px touch target on mobile, compact from sm: up (the design
 * system's density rule). The state is carried by aria-checked and a visible
 * On/Off word, never by colour alone.
 */
interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The accessible name, e.g. "Active: Dana Moss". */
  label: string;
  disabled?: boolean;
  /** Show "On"/"Off" (or the given words) beside the switch. */
  showState?: boolean;
  onText?: string;
  offText?: string;
  testId?: string;
}

export default function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  showState = false,
  onText = 'On',
  offText = 'Off',
  testId,
}: ToggleProps) {
  return (
    <span className="inline-flex min-h-[44px] items-center gap-2 sm:min-h-0">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 ${
          checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-150 ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      {showState && (
        <span aria-hidden="true" className="text-xs font-medium text-gray-600 dark:text-gray-300">
          {checked ? onText : offText}
        </span>
      )}
    </span>
  );
}
